<template>
  <div class="admin-page">
    <header class="admin-header">
      <router-link class="wordmark" to="/">TRA<span>O</span>UT<i>®</i></router-link>
      <span>CONTENT STUDIO / CMS</span>
      <router-link to="/">返回门户 ↗</router-link>
    </header>

    <section v-if="!authenticated" class="admin-login">
      <el-card class="login-card" shadow="never">
        <span class="eyebrow">PRIVATE WORKSPACE</span>
        <h1>运营后台</h1>
        <p>使用管理员账号登录。</p>
        <el-form @submit.native.prevent="login">
          <el-form-item>
            <el-input v-model.trim="usernameInput" required autocomplete="username" placeholder="管理员账号" />
          </el-form-item>
          <el-form-item>
            <el-input v-model="passwordInput" required type="password" autocomplete="current-password" placeholder="密码" @keyup.enter.native="login" />
          </el-form-item>
          <el-button class="submit-button" type="primary" :loading="busy" @click="login">{{ busy ? '正在验证…' : '登录后台' }} ↗</el-button>
          <el-alert v-if="error" class="login-error" :title="error" type="error" :closable="false" />
        </el-form>
      </el-card>
    </section>

    <el-container v-else class="admin-workspace">
      <el-aside width="244px" class="admin-sidebar">
        <div class="sidebar-brand">
          <span class="eyebrow">WORKSPACE</span>
          <h1>内容管理</h1>
        </div>
        <el-menu :default-active="activeMenuKey" :default-openeds="openMenus" class="admin-menu" @select="selectMenu">
          <el-submenu index="contents">
            <template slot="title"><i class="el-icon-document" /><span>页面配置</span></template>
            <el-menu-item v-for="page in pageGroups" :key="page.id" :index="`contents:${page.id}`">{{ page.label }} <span class="menu-count">{{ contentCount(page.id) }}</span></el-menu-item>
          </el-submenu>
          <el-menu-item index="navigation"><i class="el-icon-menu" /><span slot="title">导航菜单</span></el-menu-item>
          <el-menu-item index="products"><i class="el-icon-goods" /><span slot="title">产品管理</span></el-menu-item>
          <el-menu-item v-if="currentUser.role !== 'VIEWER'" index="categories"><i class="el-icon-collection-tag" /><span slot="title">产品分类</span></el-menu-item>
          <el-menu-item v-if="currentUser.role !== 'VIEWER'" index="inquiries"><i class="el-icon-chat-line-round" /><span slot="title">预约与线索</span></el-menu-item>
          <el-menu-item v-if="currentUser.role === 'ADMIN'" index="users"><i class="el-icon-user" /><span slot="title">管理员账号</span></el-menu-item>
          <el-menu-item v-if="currentUser.role === 'ADMIN'" index="emailSettings"><i class="el-icon-message" /><span slot="title">邮箱设置</span></el-menu-item>
        </el-menu>
        <el-button class="logout-button" type="text" icon="el-icon-switch-button" @click="logout">退出管理</el-button>
      </el-aside>

      <el-container class="admin-content">
        <el-header class="admin-topbar">
          <el-breadcrumb separator="/">
            <el-breadcrumb-item>TRAOUT CMS</el-breadcrumb-item>
            <el-breadcrumb-item v-if="activeTab === 'contents'">页面配置</el-breadcrumb-item>
            <el-breadcrumb-item>{{ activeTab === 'contents' ? currentPage.label : currentTab.label }}</el-breadcrumb-item>
          </el-breadcrumb>
          <span class="admin-account"><i class="el-icon-user" /> {{ currentUser.username }} · {{ roleName(currentUser.role) }}</span>
        </el-header>

        <el-main class="admin-main">
          <div class="admin-heading">
            <div>
              <span class="eyebrow">TRAOUT / {{ activeTab === 'contents' ? currentPage.label : currentTab.label }}</span>
              <h2>{{ activeTab === 'contents' ? currentPage.label : currentTab.label }}</h2>
            </div>
            <div class="heading-actions">
              <el-button v-if="activeTab === 'inquiries'" @click="exportInquiries">导出 CSV ↓</el-button>
              <el-button v-if="canEdit && !['inquiries', 'users', 'emailSettings'].includes(activeTab)" type="primary" @click="newItem">＋ 新建</el-button>
            </div>
          </div>
          <el-alert v-if="notice" class="admin-notice" :title="notice" type="info" :closable="true" @close="notice = ''" />
          <el-alert v-if="loadError" class="admin-notice" :title="loadError" type="error" :closable="false" show-icon />

          <div v-if="activeTab === 'products'" class="product-tools">
            <span>共 {{ products.length }} 件商品</span>
            <el-input v-model.trim="productSearch" clearable prefix-icon="el-icon-search" placeholder="搜索商品名称或分类" />
            <el-select v-model="productStatus" aria-label="商品状态筛选">
              <el-option label="全部状态" value="all" />
              <el-option label="已上架" value="enabled" />
              <el-option label="已下架" value="disabled" />
            </el-select>
          </div>

          <div v-if="activeTab === 'contents'" v-loading="loading" class="admin-table record-list">
            <div class="record-header content-record">
              <span>配置项</span><span>内容标识</span><span>状态</span><span>操作</span>
            </div>
            <div v-for="item in visibleContents" :key="item.contentKey" class="record-row content-record">
              <strong>{{ item.title || item.contentKey }}</strong>
              <span class="record-key">{{ item.contentKey }}</span>
              <span><el-tag :type="item.enabled ? 'success' : 'info'" size="mini">{{ item.enabled ? '已发布' : '已隐藏' }}</el-tag></span>
              <span class="record-actions" v-if="canEdit"><el-button type="text" @click="edit(item)">编辑</el-button><el-button type="text" class="danger-text" @click="remove('contents', item.contentKey)">删除</el-button></span>
            </div>
            <el-empty v-if="!loading && !visibleContents.length" description="此页面暂时没有配置内容" />
          </div>

          <el-table v-if="activeTab === 'navigation'" :key="tableRenderKey" :data="navigation" v-loading="loading" class="admin-table" empty-text="暂无导航菜单">
            <el-table-column label="菜单名称" min-width="160">
              <template slot-scope="scope"><strong>{{ scope.row.parentId ? '↳ ' : '' }}{{ scope.row.label }}</strong></template>
            </el-table-column>
            <el-table-column label="链接地址" min-width="240">
              <template slot-scope="scope"><span>{{ scope.row.external ? '外部链接' : '站内路由' }} · {{ scope.row.target }}</span></template>
            </el-table-column>
            <el-table-column label="状态" width="100">
              <template slot-scope="scope"><el-tag :type="scope.row.enabled ? 'success' : 'info'" size="mini">{{ scope.row.enabled ? '启用' : '停用' }}</el-tag></template>
            </el-table-column>
            <el-table-column label="操作" width="150" fixed="right">
              <template slot-scope="scope"><el-button v-if="canEdit" type="text" @click="edit(scope.row)">编辑</el-button><el-button v-if="canEdit" type="text" class="danger-text" @click="remove('navigation', scope.row.id)">删除</el-button></template>
            </el-table-column>
          </el-table>

          <div v-if="activeTab === 'products'" v-loading="loading" class="admin-table record-list">
            <div class="record-header product-record">
              <span>产品</span><span>价格</span><span>状态</span><span>操作</span>
            </div>
            <div v-for="item in visibleProducts" :key="item.id" class="record-row product-record">
              <div class="product-cell">
                <el-image v-if="item.imageUrl" :src="item.imageUrl" fit="cover" class="product-thumb" />
                <span v-else class="product-thumb product-thumb-empty"><i class="el-icon-picture-outline" /></span>
                <div><strong>{{ item.name }}</strong><span>{{ item.category }}</span></div>
              </div>
              <strong>¥{{ item.price }}</strong>
              <span><el-tag :type="item.enabled ? 'success' : 'info'" size="mini">{{ item.enabled ? '已上架' : '已下架' }}</el-tag></span>
              <span class="record-actions" v-if="canEdit"><el-button type="text" @click="edit(item)">编辑</el-button><el-button type="text" class="danger-text" @click="remove('products', item.id)">删除</el-button></span>
            </div>
            <el-empty v-if="!loading && !visibleProducts.length" description="暂无符合条件的商品，请先新建商品或调整筛选条件" />
          </div>

          <el-table v-if="activeTab === 'categories'" :key="tableRenderKey" :data="categories" v-loading="loading" class="admin-table" empty-text="暂无产品分类">
            <el-table-column label="分类名称" min-width="200">
              <template slot-scope="scope"><strong>{{ scope.row.parentId ? '↳ ' : '' }}{{ scope.row.name }}</strong></template>
            </el-table-column>
            <el-table-column label="所属层级" min-width="160">
              <template slot-scope="scope">{{ scope.row.parentId ? scope.row.parentName : '产品线' }}</template>
            </el-table-column>
            <el-table-column label="状态" width="110">
              <template slot-scope="scope"><el-tag :type="scope.row.enabled ? 'success' : 'info'" size="mini">{{ scope.row.enabled ? '启用' : '停用' }}</el-tag></template>
            </el-table-column>
            <el-table-column label="操作" width="150" fixed="right">
              <template slot-scope="scope"><el-button v-if="canEdit" type="text" @click="edit(scope.row)">编辑</el-button><el-button v-if="canEdit" type="text" class="danger-text" @click="remove('categories', scope.row.id)">删除</el-button></template>
            </el-table-column>
          </el-table>

          <div v-if="activeTab === 'inquiries'" class="inquiry-tools">
            <el-select v-model="inquiryType" aria-label="线索类型筛选">
              <el-option label="全部类型" value="all" />
              <el-option label="预约" value="reservation" />
              <el-option label="渠道合作" value="cooperation" />
              <el-option label="简历投递" value="resume" />
            </el-select>
            <el-select v-model="inquiryStatus" aria-label="线索状态筛选">
              <el-option label="全部状态" value="all" />
              <el-option label="待跟进" value="待跟进" />
              <el-option label="已联系" value="已联系" />
              <el-option label="已转化" value="已转化" />
              <el-option label="已关闭" value="已关闭" />
            </el-select>
            <el-input v-model.trim="inquirySearch" clearable prefix-icon="el-icon-search" placeholder="搜索联系人、电话或邮箱" />
          </div>

          <el-table v-if="activeTab === 'inquiries'" :key="tableRenderKey" :data="visibleInquiries" v-loading="loading" class="admin-table" empty-text="暂无预约与线索">
            <el-table-column label="联系人" min-width="170">
              <template slot-scope="scope"><strong>{{ scope.row.name }}</strong><span class="table-subtitle">{{ inquiryTypeName(scope.row.type) }} · {{ scope.row.phone }}</span><span v-if="scope.row.age || scope.row.birthday" class="table-subtitle">{{ scope.row.age ? `${scope.row.age} 岁` : '' }}{{ scope.row.age && scope.row.birthday ? ' · ' : '' }}{{ scope.row.birthday || '' }}</span></template>
            </el-table-column>
            <el-table-column label="提交信息" min-width="260">
              <template slot-scope="scope"><span>{{ scope.row.email || '未填写邮箱' }} · {{ scope.row.createdAt }}</span><span class="table-subtitle">{{ scope.row.payload }}</span><el-button v-if="scope.row.resumePath" type="text" @click="downloadResume(scope.row)">下载简历 ↓</el-button></template>
            </el-table-column>
            <el-table-column label="跟进状态" width="150">
              <template slot-scope="scope"><el-select :value="scope.row.status" size="small" aria-label="跟进状态" @change="updateStatus(scope.row.id, $event)"><el-option label="待跟进" value="待跟进" /><el-option label="已联系" value="已联系" /><el-option label="已转化" value="已转化" /><el-option label="已关闭" value="已关闭" /></el-select></template>
            </el-table-column>
          </el-table>

          <div v-if="activeTab === 'users'" class="users-panel">
            <el-card class="user-create" shadow="never">
              <div slot="header"><span class="eyebrow">ADD TEAM MEMBER</span><strong>创建管理员账号</strong></div>
              <el-form label-position="top" class="user-form">
                <el-form-item label="账号"><el-input v-model.trim="userForm.username" autocomplete="off" /></el-form-item>
                <el-form-item label="初始密码"><el-input v-model="userForm.password" type="password" autocomplete="new-password" /></el-form-item>
                <el-form-item label="角色"><el-select v-model="userForm.role"><el-option label="只读" value="VIEWER" /><el-option label="编辑" value="EDITOR" /><el-option label="管理员" value="ADMIN" /></el-select></el-form-item>
                <el-button type="primary" @click="createUser">创建账号 ↗</el-button>
              </el-form>
            </el-card>
            <el-table :key="tableRenderKey" :data="users" v-loading="loading" class="admin-table" empty-text="暂无管理员账号">
              <el-table-column prop="username" label="账号" min-width="140" />
              <el-table-column label="角色" width="170">
                <template slot-scope="scope"><el-select :value="scope.row.role" :disabled="scope.row.username === currentUser.username" size="small" @change="updateUser(scope.row, { role: $event })"><el-option label="只读" value="VIEWER" /><el-option label="编辑" value="EDITOR" /><el-option label="管理员" value="ADMIN" /></el-select></template>
              </el-table-column>
              <el-table-column label="状态" width="100">
                <template slot-scope="scope">{{ scope.row.enabled ? '已启用' : '已停用' }}</template>
              </el-table-column>
              <el-table-column label="操作" width="200" fixed="right">
                <template slot-scope="scope"><el-button type="text" :disabled="scope.row.username === currentUser.username" @click="updateUser(scope.row, { enabled: !scope.row.enabled })">{{ scope.row.enabled ? '停用' : '启用' }}</el-button><el-button type="text" @click="resetPassword(scope.row)">重置密码</el-button></template>
              </el-table-column>
            </el-table>
          </div>

          <el-card v-if="activeTab === 'emailSettings'" class="email-settings-panel" shadow="never">
            <span class="eyebrow">SMTP / RESUME DELIVERY</span>
            <h3>简历收件邮箱</h3>
            <p>访客提交的简历将作为附件发送到此 QQ 邮箱。此设置仅管理员可见。</p>
            <el-form label-position="top" @submit.native.prevent="saveEmailSettings">
              <el-form-item label="简历及线索收件邮箱（QQ 邮箱）">
                <el-input v-model.trim="emailSettings.recipientEmail" type="email" maxlength="254" autocomplete="email" placeholder="例如：123456789@qq.com" />
              </el-form-item>
              <el-button type="primary" :loading="savingEmailSettings" @click="saveEmailSettings">保存收件邮箱</el-button>
            </el-form>
            <el-divider />
            <h3>QQ 邮箱发件配置</h3>
            <p>在此填写发件 QQ 邮箱及 SMTP 授权码即可启用邮件发送，无需修改服务器环境变量。</p>
            <el-form label-position="top" @submit.native.prevent="saveEmailSettings">
              <el-form-item label="SMTP 服务器">
                <el-input v-model.trim="emailSettings.smtpHost" placeholder="smtp.qq.com" />
              </el-form-item>
              <el-form-item label="SMTP 端口">
                <el-input v-model.trim="emailSettings.smtpPort" placeholder="587（STARTTLS）/ 465（SSL）" maxlength="5" />
              </el-form-item>
              <el-form-item label="发件邮箱（QQ 邮箱）">
                <el-input v-model.trim="emailSettings.smtpUsername" type="email" maxlength="254" autocomplete="email" placeholder="例如：123456789@qq.com" />
              </el-form-item>
              <el-form-item label="SMTP 授权码">
                <el-input v-model="emailSettings.smtpPassword" type="password" maxlength="64" autocomplete="new-password" :placeholder="emailSettings.smtpPasswordSet ? '已设置，留空保持不变' : '输入 QQ 邮箱 SMTP 授权码'" />
              </el-form-item>
              <el-alert
                :title="emailSettings.smtpConfigured ? '已配置 SMTP 发件服务' : '尚未配置 SMTP 发件服务，请填写上方发件配置后保存。'"
                :type="emailSettings.smtpConfigured ? 'success' : 'warning'"
                :closable="false"
                show-icon
              />
              <div class="email-actions">
                <el-button type="primary" :loading="savingEmailSettings" @click="saveEmailSettings">保存邮箱配置</el-button>
                <el-button type="primary" plain :loading="testingEmail" :disabled="!emailSettings.smtpConfigured" @click="sendTestEmail">发送测试邮件</el-button>
              </div>
              <p class="field-hint">QQ 邮箱需先在「设置 → 账户 → 开启 SMTP 服务」生成授权码；授权码保存在本系统数据库中，不会明文回显。</p>
            </el-form>
          </el-card>
        </el-main>
      </el-container>

      <el-dialog :title="modalTitle" :visible="Boolean(editing)" width="620px" top="7vh" @close="editing = null">
        <el-form v-if="editing" :model="editing" label-position="top" class="edit-form">
          <template v-if="activeTab === 'contents'">
            <el-form-item :label="selectedContentPage === 'mall' ? '商城入口标识' : '内容标识'">
              <el-input v-model="editing.key" :disabled="Boolean(editing.contentKey)" :placeholder="selectedContentPage === 'mall' ? 'mall.store.taobao' : ''" />
              <span v-if="selectedContentPage === 'mall'" class="field-hint">标识需以 mall.store. 开头，用于区分多个商城入口。</span>
            </el-form-item>
            <el-form-item :label="selectedContentPage === 'mall' ? '商城名称' : '标题'"><el-input v-model="editing.title" /></el-form-item>
            <el-form-item :label="selectedContentPage === 'mall' ? '外部商城链接' : '正文'">
              <el-input v-model="editing.body" :type="selectedContentPage === 'mall' ? 'url' : 'textarea'" :rows="5" :placeholder="selectedContentPage === 'mall' ? 'https://example.com' : ''" />
              <span v-if="selectedContentPage === 'mall'" class="field-hint">点击商城图片后将在新窗口打开此链接（仅支持 HTTP / HTTPS）。</span>
            </el-form-item>
            <el-form-item :label="selectedContentPage === 'mall' ? '商城展示图片' : '图片'"><image-upload v-model="editing.imageUrl" @error="showUploadError" /></el-form-item>
            <el-form-item label="排序"><el-input-number v-model="editing.sortOrder" controls-position="right" /></el-form-item>
            <el-form-item><el-checkbox v-model="editing.enabled">发布到门户</el-checkbox></el-form-item>
          </template>
          <template v-else-if="activeTab === 'navigation'">
            <el-form-item label="菜单名称"><el-input v-model="editing.label" /></el-form-item>
            <el-form-item label="父级菜单"><el-select v-model="editing.parentId" clearable placeholder="一级菜单"><el-option v-for="item in navigation.filter(entry => !entry.parentId && entry.id !== editing.id)" :key="item.id" :label="item.label" :value="item.id" /></el-select></el-form-item>
            <el-form-item label="跳转地址"><el-input v-model="editing.target" placeholder="/products 或 https://example.com" /></el-form-item>
            <el-form-item><el-checkbox v-model="editing.external">外部网站（新窗口打开）</el-checkbox></el-form-item>
            <el-form-item label="排序"><el-input-number v-model="editing.sortOrder" controls-position="right" /></el-form-item>
            <el-form-item><el-checkbox v-model="editing.enabled">启用菜单</el-checkbox></el-form-item>
          </template>
          <template v-else-if="activeTab === 'products'">
            <el-form-item label="产品名称"><el-input v-model="editing.name" /></el-form-item>
            <el-form-item label="产品分类"><el-select v-model="editing.category" placeholder="请选择分类"><el-option v-for="category in categories" :key="category.id" :label="category.path" :value="String(category.id)" /></el-select><span class="field-hint">没有合适的分类？先到“产品分类”中创建。</span></el-form-item>
            <el-form-item label="价格"><el-input-number v-model="editing.price" :min="0" :step="0.01" :precision="2" controls-position="right" /></el-form-item>
            <el-form-item label="产品图片"><image-upload v-model="editing.imageUrl" @error="showUploadError" /></el-form-item>
            <el-form-item label="描述"><el-input v-model="editing.description" type="textarea" :rows="4" /></el-form-item>
            <el-form-item label="购买平台"><el-input v-model="editing.purchasePlatform" placeholder="淘宝 / 抖音 / 京东" /></el-form-item>
            <el-form-item label="商品链接"><el-input v-model="editing.purchaseUrl" /></el-form-item>
            <el-form-item label="排序"><el-input-number v-model="editing.sortOrder" controls-position="right" /></el-form-item>
            <el-form-item><el-checkbox v-model="editing.enabled">上架</el-checkbox></el-form-item>
          </template>
          <template v-else>
            <el-form-item label="分类名称"><el-input v-model.trim="editing.name" /></el-form-item>
            <el-form-item label="父级产品线"><el-select v-model="editing.parentId" clearable placeholder="一级产品线"><el-option v-for="item in categories.filter(entry => !entry.parentId && entry.id !== editing.id)" :key="item.id" :label="item.name" :value="item.id" /></el-select></el-form-item>
            <el-form-item label="排序"><el-input-number v-model="editing.sortOrder" controls-position="right" /></el-form-item>
            <el-form-item><el-checkbox v-model="editing.enabled">启用分类</el-checkbox></el-form-item>
          </template>
        </el-form>
        <span slot="footer" class="dialog-footer">
          <el-button @click="editing = null">取消</el-button>
          <el-button type="primary" @click="saveEditing">保存更改 ↗</el-button>
        </span>
      </el-dialog>
    </el-container>
  </div>
</template>

<script>
import { adminRequest, downloadAdminFile, getAdminSession, loginAdmin, logoutAdmin, uploadAdminImage } from '../api'

const ImageUpload = {
  props: { value: { type: String, default: '' } },
  data: () => ({ uploading: false }),
  render(h) {
    const uploadButton = h('el-upload', {
      props: {
        action: '#',
        accept: 'image/jpeg,image/png,image/gif',
        showFileList: false,
        httpRequest: this.upload,
        onError: this.uploadFailed
      }
    }, [h('el-button', {
      props: { size: 'small', loading: this.uploading }
    }, [this.uploading ? '正在上传…' : '上传图片'])])

    const children = [
      this.value ? h('el-image', {
        class: 'image-preview',
        props: { src: this.value, fit: 'cover' }
      }) : null,
      h('div', { class: 'image-upload-actions' }, [
        uploadButton,
        h('el-input', {
          props: { value: this.value, placeholder: '上传后自动填入图片链接' },
          on: { input: value => this.$emit('input', value) }
        }),
        h('span', { class: 'field-hint' }, ['支持 JPG、PNG、GIF，最大 10 MB；也可以直接粘贴图片链接。'])
      ])
    ]
    return h('div', { class: 'image-upload' }, children)
  },
  methods: {
    async upload(options) {
      this.uploading = true
      try {
        const result = await uploadAdminImage(options.file)
        this.$emit('input', result.url)
        options.onSuccess(result)
      } catch (error) {
        options.onError(error)
      } finally {
        this.uploading = false
      }
    },
    uploadFailed(error) {
      this.$emit('error', error)
    }
  }
}

export default {
  components: { ImageUpload },
  data: () => ({
    usernameInput: '',
    passwordInput: '',
    authenticated: false,
    busy: false,
    currentUser: {},
    error: '',
    notice: '',
    loadError: '',
    loading: false,
    tableVersion: 0,
    activeTab: 'contents',
    selectedContentPage: 'home',
    openMenus: ['contents'],
    productSearch: '',
    productStatus: 'all',
    inquiryType: 'all',
    inquiryStatus: 'all',
    inquirySearch: '',
    pageGroups: [
      { id: 'all', label: '全部配置', keys: [] },
      { id: 'home', label: '首页', keys: ['home.', 'category.'] },
      { id: 'story', label: '品牌故事', keys: ['brand.'] },
      { id: 'products', label: '产品系列', keys: ['products.'] },
      { id: 'reservation', label: '预约服务', keys: ['reservation.'] },
      { id: 'cooperation', label: '渠道合作', keys: ['cooperation.'] },
      { id: 'careers', label: '加入我们', keys: ['jobs.'] },
      { id: 'mall', label: '线上商城', keys: ['mall.store.'] },
      { id: 'contact', label: '联系我们', keys: ['store.info', 'footer.info'] },
      { id: 'other', label: '其他内容', keys: [] }
    ],
    contents: [],
    navigation: [],
    products: [],
    categories: [],
    inquiries: [],
    users: [],
    emailSettings: { recipientEmail: '', smtpHost: '', smtpPort: '', smtpUsername: '', smtpPassword: '', smtpPasswordSet: false, smtpConfigured: false },
    savingEmailSettings: false,
    testingEmail: false,
    editing: null,
    userForm: { username: '', password: '', role: 'VIEWER' }
  }),
  computed: {
    tabs() {
      const items = [{ id: 'contents', label: '页面配置' }, { id: 'navigation', label: '导航菜单' }, { id: 'products', label: '产品管理' }]
      if (this.currentUser.role !== 'VIEWER') items.push({ id: 'categories', label: '产品分类' }, { id: 'inquiries', label: '预约与线索' })
      if (this.currentUser.role === 'ADMIN') items.push({ id: 'users', label: '管理员账号' }, { id: 'emailSettings', label: '邮箱设置' })
      return items
    },
    currentTab() { return this.tabs.find(tab => tab.id === this.activeTab) || this.tabs[0] },
    canEdit() { return this.currentUser.role !== 'VIEWER' },
    currentPage() { return this.pageGroups.find(page => page.id === this.selectedContentPage) || this.pageGroups[0] },
    activeMenuKey() { return this.activeTab === 'contents' ? `contents:${this.selectedContentPage}` : this.activeTab },
    tableRenderKey() {
      return [this.activeTab, this.selectedContentPage, this.tableVersion, this.productSearch, this.productStatus].join(':')
    },
    contentCount() {
      return pageId => {
        if (pageId === 'all') return this.contents.length
        if (pageId === 'other') return this.contents.filter(item => !this.pageGroups.slice(1, -1).some(page => page.keys.some(key => item.contentKey.startsWith(key)))).length
        const page = this.pageGroups.find(group => group.id === pageId)
        return page ? this.contents.filter(item => page.keys.some(key => item.contentKey.startsWith(key))).length : 0
      }
    },
    visibleContents() {
      if (this.selectedContentPage === 'all') return this.contents
      if (this.selectedContentPage === 'other') {
        return this.contents.filter(item => !this.pageGroups.slice(1, -1).some(page => page.keys.some(key => item.contentKey.startsWith(key))))
      }
      const page = this.currentPage
      return this.contents.filter(item => page.keys.some(key => item.contentKey.startsWith(key)))
    },
    visibleProducts() {
      const query = this.productSearch.toLocaleLowerCase()
      return this.products.filter(product => {
        const matchesQuery = !query || `${product.name} ${product.category}`.toLocaleLowerCase().includes(query)
        const matchesStatus = this.productStatus === 'all'
          || (this.productStatus === 'enabled' && Boolean(product.enabled))
          || (this.productStatus === 'disabled' && !product.enabled)
        return matchesQuery && matchesStatus
      })
    },
    visibleInquiries() {
      const query = this.inquirySearch.toLocaleLowerCase()
      return this.inquiries.filter(inquiry => {
        const matchesStatus = this.inquiryStatus === 'all' || inquiry.status === this.inquiryStatus
        const matchesQuery = !query || `${inquiry.name || ''} ${inquiry.phone || ''} ${inquiry.email || ''} ${inquiry.payload || ''}`.toLocaleLowerCase().includes(query)
        return matchesStatus && matchesQuery
      })
    },
    modalTitle() { return ({ contents: '编辑页面内容', navigation: '编辑导航菜单', products: '编辑产品信息', categories: '编辑产品分类' })[this.activeTab] || '编辑信息' }
  },
  watch: {
    activeTab() { this.editing = null; if (this.authenticated) this.load() },
    inquiryType() { if (this.authenticated && this.activeTab === 'inquiries') this.load() }
  },
  created() { if (localStorage.getItem('traout-admin-token')) this.restoreSession() },
  methods: {
    selectMenu(key) {
      if (key.startsWith('contents:')) {
        this.selectedContentPage = key.slice('contents:'.length)
        this.activeTab = 'contents'
      } else {
        this.activeTab = key
      }
    },
    async restoreSession() {
      try {
        this.currentUser = await getAdminSession()
        this.authenticated = true
        await this.load()
      } catch (error) {
        this.error = this.errorMessage(error, '登录状态已失效，请重新登录。')
        await this.logout()
        this.error = '登录状态已失效，请重新登录。'
      }
    },
    async login() {
      this.busy = true
      this.error = ''
      try {
        const session = await loginAdmin({ username: this.usernameInput, password: this.passwordInput })
        localStorage.setItem('traout-admin-token', session.token)
        this.currentUser = { username: session.username, role: session.role }
        this.authenticated = true
        this.passwordInput = ''
        await this.load()
      } catch (error) {
        this.error = this.errorMessage(error, '账号、密码错误或后台服务不可用。')
      } finally {
        this.busy = false
      }
    },
    async logout() {
      try { await logoutAdmin() } catch (error) { /* Clear the local session even when the server is unavailable. */ }
      localStorage.removeItem('traout-admin-token')
      this.currentUser = {}
      this.authenticated = false
    },
    async load() {
      this.loading = true
      this.loadError = ''
      const map = { contents: '/contents', navigation: '/navigation', products: '/products', categories: '/categories', inquiries: '/inquiries', users: '/users', emailSettings: '/settings/email' }
      try {
        const params = this.activeTab === 'inquiries' && this.inquiryType !== 'all' ? { type: this.inquiryType } : undefined
        const data = await adminRequest('get', map[this.activeTab], undefined, params)
        if (this.activeTab === 'emailSettings') {
          if (!data || typeof data.recipientEmail !== 'string') throw new Error('邮箱设置接口返回格式异常')
          this.emailSettings = data
        } else {
          if (!Array.isArray(data)) throw new Error(`${this.currentTab.label}接口返回格式异常`)
          this[this.activeTab] = data
          if (this.activeTab === 'products') this.categories = await adminRequest('get', '/categories')
        }
        this.tableVersion += 1
      } catch (error) {
        this.loadError = this.errorMessage(error, `${this.currentTab.label}加载失败，请检查后台服务和账号权限。`)
      } finally {
        this.loading = false
      }
    },
    newItem() {
      if (this.activeTab === 'contents') this.editing = {
        key: this.selectedContentPage === 'mall' ? `mall.store.store-${Date.now()}` : '',
        title: '',
        body: '',
        imageUrl: '',
        enabled: true,
        sortOrder: 0
      }
      else if (this.activeTab === 'navigation') this.editing = { label: '', target: '', parentId: null, external: false, enabled: true, sortOrder: 0 }
      else if (this.activeTab === 'categories') this.editing = { name: '', parentId: null, enabled: true, sortOrder: 0 }
      else this.editing = { name: '', category: '', price: 0, description: '', imageUrl: '', purchaseUrl: '', purchasePlatform: '', enabled: true, sortOrder: 0 }
    },
    edit(item) {
      this.editing = this.activeTab === 'contents'
        ? { ...item, key: item.contentKey, enabled: Boolean(item.enabled) }
        : this.activeTab === 'products'
          ? { ...item, category: String(item.categoryId || ''), enabled: Boolean(item.enabled) }
          : this.activeTab === 'navigation'
            ? { ...item, parentId: item.parentId || null, external: Boolean(item.external), enabled: Boolean(item.enabled) }
            : { ...item, parentId: item.parentId || null, enabled: Boolean(item.enabled) }
    },
    async saveEditing() {
      if (this.activeTab === 'contents' && this.selectedContentPage === 'mall') {
        if (!this.editing.key.startsWith('mall.store.')) {
          this.notice = '商城入口标识必须以 mall.store. 开头。'
          return
        }
        if (!this.editing.title.trim() || !this.isSafeImageUrl(this.editing.imageUrl) || !this.isSafeExternalUrl(this.editing.body)) {
          this.notice = '请填写商城名称、展示图片和有效的 HTTP / HTTPS 外链。'
          return
        }
      }
      const paths = { contents: '/contents', navigation: '/navigation', products: '/products', categories: '/categories' }
      const method = this.activeTab === 'categories' && this.editing.id ? 'put' : this.activeTab === 'categories' ? 'post' : 'put'
      const path = this.activeTab === 'categories' && this.editing.id ? `/categories/${this.editing.id}` : paths[this.activeTab]
      await this.save(path, this.editing, method)
      if (!this.editing) await this.load()
    },
    isSafeExternalUrl(value) {
      try {
        const url = new URL(value)
        return ['http:', 'https:'].includes(url.protocol) && Boolean(url.hostname) && !url.username && !url.password
      } catch (error) {
        return false
      }
    },
    isSafeImageUrl(value) {
      if (/^https?:\/\//i.test(value || '')) return this.isSafeExternalUrl(value)
      return Boolean(value && value.startsWith('/') && !value.startsWith('//'))
    },
    async save(path, item, method = 'put') {
      try {
        await adminRequest(method, path, item)
        this.editing = null
        this.notice = '已保存。'
      } catch (error) {
        this.notice = error.response && error.response.data.message ? error.response.data.message : '保存失败，请检查字段。'
      }
    },
    async remove(type, id) {
      if (!window.confirm('确定删除这条记录？')) return
      try {
        const resourceId = type === 'contents' ? encodeURIComponent(id) : id
        await adminRequest('delete', `/${type}/${resourceId}`)
        await this.load()
      } catch (error) {
        this.notice = error.response && error.response.data.message ? error.response.data.message : '删除失败。'
      }
    },
    async updateStatus(id, status) {
      try {
        await adminRequest('patch', `/inquiries/${id}/status`, { status })
        const inquiry = this.inquiries.find(item => item.id === id)
        if (inquiry) this.$set(inquiry, 'status', status)
        this.notice = '跟进状态已更新。'
      } catch (error) {
        this.notice = error.response && error.response.data.message ? error.response.data.message : '状态更新失败。'
      }
    },
    inquiryTypeName(type) { return ({ reservation: '预约', cooperation: '渠道合作', resume: '简历投递' })[type] || type || '其他' },
    roleName(role) { return ({ ADMIN: '管理员', EDITOR: '编辑', VIEWER: '只读' })[role] || role },
    showUploadError(error) {
      this.notice = this.errorMessage(error, '图片上传失败，请检查登录状态、图片格式和 10 MB 大小限制。')
    },
    errorMessage(error, fallback) {
      const responseMessage = error.response && error.response.data && error.response.data.message
      if (responseMessage) return responseMessage
      if (error.response && error.response.status === 401) return '登录已过期，请重新登录后台。'
      if (error.response && error.response.status === 403) return '当前账号没有此操作权限。'
      if (error.response && error.response.status === 413) return '文件超过服务器上传大小限制。'
      if (error.message && error.message !== 'Network Error') return error.message
      return fallback
    },
    async createUser() {
      if (!this.userForm.username || !this.userForm.password) {
        this.notice = '请填写账号和初始密码。'
        return
      }
      try {
        await adminRequest('post', '/users', this.userForm)
        this.userForm = { username: '', password: '', role: 'VIEWER' }
        this.notice = '管理员账号已创建。'
        await this.load()
      } catch (error) {
        this.notice = error.response && error.response.data.message ? error.response.data.message : '账号创建失败，请检查账号是否重复。'
      }
    },
    async updateUser(user, changes) {
      try {
        await adminRequest('put', `/users/${user.id}`, changes)
        await this.load()
      } catch (error) {
        this.notice = error.response && error.response.data.message ? error.response.data.message : '账号更新失败。'
      }
    },
    async resetPassword(user) {
      const password = window.prompt(`请输入 ${user.username} 的新密码`)
      if (!password) return
      await this.updateUser(user, { password })
    },
    async saveEmailSettings() {
      if (!this.emailSettings.recipientEmail || !/^[A-Za-z0-9._%+-]+@qq\.com$/i.test(this.emailSettings.recipientEmail)) {
        this.notice = '收件邮箱必须为 QQ 邮箱格式（例如：123456789@qq.com）。'
        return
      }
      if (this.emailSettings.smtpPort && !/^\d+$/.test(this.emailSettings.smtpPort)) {
        this.notice = 'SMTP 端口必须为数字。'
        return
      }
      if (this.emailSettings.smtpUsername && !this.emailSettings.smtpHost) {
        this.notice = '填写发件邮箱后请同时填写 SMTP 服务器。'
        return
      }
      this.savingEmailSettings = true
      try {
        await adminRequest('put', '/settings/email', {
          recipientEmail: this.emailSettings.recipientEmail,
          smtpHost: this.emailSettings.smtpHost || '',
          smtpPort: this.emailSettings.smtpPort || '',
          smtpUsername: this.emailSettings.smtpUsername || '',
          smtpPassword: this.emailSettings.smtpPassword || ''
        })
        const fresh = await adminRequest('get', '/settings/email')
        this.emailSettings = fresh
        this.emailSettings.smtpPassword = ''
        this.notice = '邮箱配置已保存。'
      } catch (error) {
        this.notice = this.errorMessage(error, '邮箱配置保存失败。')
      } finally {
        this.savingEmailSettings = false
      }
    },
    async sendTestEmail() {
      this.testingEmail = true
      try {
        const response = await adminRequest('post', '/settings/email/test')
        this.notice = (response && response.message) || '测试邮件已发送，请检查收件邮箱。'
      } catch (error) {
        this.notice = this.errorMessage(error, '测试邮件发送失败。')
      } finally {
        this.testingEmail = false
      }
    },
    async downloadResume(item) {
      try { await downloadAdminFile(`/inquiries/${item.id}/resume`, `TRAOUT-${item.name}-resume`) } catch (error) { this.notice = '简历下载失败。' }
    },
    async exportInquiries() {
      try { await downloadAdminFile('/inquiries/export', 'TRAOUT-线索.csv') } catch (error) { this.notice = '线索导出失败。' }
    }
  }
}
</script>
